import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { TenantContextService } from './tenant-context.service';
import { PlanEntitlements } from './subscription.service';

export interface UsageMetric {
  current: number;
  max: number | null;
}

export interface BuUsage {
  members: UsageMetric;
  activeEvents: UsageMetric;
  users: UsageMetric;
  paymentsThisMonth: UsageMetric;
  ledgerEntriesThisYear: UsageMetric;
  customFieldDefs: UsageMetric;
}

/**
 * Hydrates feature flags + caps from /api/business-units/{buId}/entitlements and exposes them as signals
 * so `*appHasFeature` and `*appWithinLimit` directives can react instantly when the BU is switched.
 */
@Injectable({ providedIn: 'root' })
export class EntitlementService {
  private api    = inject(ApiService);
  private auth   = inject(AuthService);
  private tenant = inject(TenantContextService);

  private _entitlements = signal<PlanEntitlements | null>(null);
  private _usage        = signal<BuUsage | null>(null);
  private _loadedForBuId = signal<string | null>(null);
  private _loading       = signal(false);

  readonly entitlements = this._entitlements.asReadonly();
  readonly usage = this._usage.asReadonly();
  readonly loading = this._loading.asReadonly();

  constructor() {
    effect(() => {
      const buId = this.tenant.selectedBusinessUnitId();
      if (!buId) {
        this._entitlements.set(null);
        this._usage.set(null);
        this._loadedForBuId.set(null);
        return;
      }
      if (this._loadedForBuId() === buId) return;
      this.refresh();
    });
  }

  async refresh(): Promise<void> {
    const buId = this.tenant.getCurrentBusinessUnitId();
    if (!buId) return;
    if (this._loading()) return;
    this._loading.set(true);
    try {
      const [entRes, usgRes] = await Promise.all([
        firstValueFrom(this.api.get<any>(`business-units/${buId}/entitlements`)),
        firstValueFrom(this.api.get<any>(`business-units/${buId}/usage`))
      ]);
      if (entRes.success) {
        this._entitlements.set((entRes.data as PlanEntitlements) || null);
      }
      if (usgRes.success) {
        this._usage.set((usgRes.data as BuUsage) || null);
      }
      this._loadedForBuId.set(buId);
    } finally {
      this._loading.set(false);
    }
  }

  /** Only SuperAdmin (platform operator) bypasses all gates; TenantAdmin is subject to their plan. */
  hasFeature(feature: keyof PlanEntitlements): boolean {
    if (this.auth.isSuperAdmin()) return true;
    const ent = this._entitlements();
    if (!ent) return false;
    const v = ent[feature];
    return typeof v === 'boolean' ? v : false;
  }

  /** Returns true when current usage is under the cap or the cap is null (unlimited). */
  withinLimit(limit: keyof BuUsage): boolean {
    if (this.auth.isSuperAdmin()) return true;
    const usg = this._usage();
    if (!usg) return false;
    const metric = usg[limit];
    if (!metric) return false;
    if (metric.max == null) return true;
    return metric.current < metric.max;
  }

  metric(limit: keyof BuUsage): UsageMetric | null {
    return this._usage()?.[limit] ?? null;
  }

  /** Percentage used (0–100). Returns 0 for unlimited or unknown caps. */
  percentUsed(limit: keyof BuUsage): number {
    const m = this.metric(limit);
    if (!m || m.max == null || m.max <= 0) return 0;
    return Math.min(100, Math.round((m.current / m.max) * 100));
  }

  /** Bumps the local cached usage by +1 after an optimistic create. Avoids a full re-fetch. */
  bumpUsage(limit: keyof BuUsage, delta = 1): void {
    const usg = this._usage();
    if (!usg) return;
    const next: BuUsage = { ...usg };
    const m = { ...next[limit] };
    m.current = Math.max(0, m.current + delta);
    next[limit] = m;
    this._usage.set(next);
  }

  reset(): void {
    this._entitlements.set(null);
    this._usage.set(null);
    this._loadedForBuId.set(null);
  }
}
